/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    type EscalatedTo,
    RequestActionType,
    type RequestNote,
    type RequestType,
} from '@strata/react-chat';
import { type GridWithFilters } from '@strata/react-grid';
import { type PayerRequestStatus } from '@strata/react-status-indicators';
import { type ComponentProps } from 'react';

import { PAYER_REQUEST_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { type RequestDetails, type TimeFrameOption } from 'services/interfaces';
import { getBaseQuery, jsonToFormData } from 'store/api/apiBaseQuery';

type RequestCounts = Record<(typeof RequestType)[keyof typeof RequestType], number>;

export interface RequestPayload {
    payerId: string;
    segmentId: string;
    requestType: (typeof RequestType)[keyof typeof RequestType];
    requestSubType?: string;
    typeLevel2?: string;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    urgent: boolean;
    pbmOnly: boolean;
    additionalNotes?: string;
    treatmentIds: string[];
    files: File[];
    assignedUpn?: string;
    payerAssignedUpn?: string;
}

export interface OpenRequestSummary {
    payerId: string;
    requestId: string;
    requestSubType?: string;
    typeLevel2?: string;
    summary?: string;
    additionalNotes?: string;
    memberId?: string;
    createDate: string;
    submitterUpn: string;
    submitterFullName?: string;
    assignedUpn?: string;
    assignedFullName?: string;
    payerAssignedUpn?: string;
    payerAssignedFullName?: string;
    issueSource?: string;
    isUnread: boolean;
    requestNumber: string;
    urgent: boolean;
    pbmOnly: boolean;
    requestStatus: PayerRequestStatus;
    escalatedTo?: (typeof EscalatedTo)[keyof typeof EscalatedTo];
    lastUpdated: string;
    resolvedDate?: string;
}

export interface ArchivedRequestSummary {
    request_id: string;
    payer_id: string;
    request_type: (typeof RequestType)[keyof typeof RequestType];
    request_sub_type?: string;
    type_level_2?: string;
    request_number: string;
    summary?: string;
    member_id?: string;
    submitter_upn: string;
    submitter_full_name?: string;
    is_unread: boolean;
    urgent: boolean;
    pbm_only: boolean;
    assigned_upn?: string;
    payer_assigned_upn?: string;
    request_status: PayerRequestStatus;
    completed_date?: string;
    resolved_at?: string;
    issue_source?: string;
    completed_by?: string;
    completed_by_full_name?: string;
}

const TagType = {
    REQUESTS_COUNTS: 'REQUESTS_COUNTS',
    REQUEST_LIST: 'REQUEST_LIST',
    REQUESTS: 'REQUESTS',
    REQUEST_NOTES: 'REQUEST_NOTES',
} as const;

export const payerRequestApi = createApi({
    baseQuery: getBaseQuery(PAYER_REQUEST_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'payerRequestApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getOpenRequestCounts: build.query<Record<string, RequestCounts>, { payerIds: string[] }>({
            queryFn: async ({ payerIds }, _queryApi, _extraOptions, baseQuery) => {
                // Make a call to get the counts for each payer
                const result = await Promise.all(
                    payerIds.map((payerId) => baseQuery(`${payerId}/requests/counts`)) as Promise<{
                        data: { open_requests: RequestCounts };
                    }>[],
                );
                return {
                    data: Object.fromEntries(
                        payerIds.map((payerId, i) => [payerId, result?.[i]?.data?.open_requests]),
                    ),
                };
            },
            providesTags: [{ type: TagType.REQUESTS_COUNTS }],
        }),
        getOpenRequestsByType: build.query<
            OpenRequestSummary[],
            {
                payerId: string;
                requestType: (typeof RequestType)[keyof typeof RequestType];
                segmentIds: string[];
            }
        >({
            query: ({ payerId, requestType, segmentIds }) => ({
                url: `${payerId}/requests`,
                params: { request_type: requestType, segment_ids: segmentIds },
            }),
            transformResponse: (
                response: {
                    filtered_count: number;
                    requests: {
                        request_id: string;
                        request_sub_type?: string;
                        type_level_2?: string;
                        summary?: string;
                        member_id?: string;
                        create_date: string;
                        submitter_upn: string;
                        is_unread: boolean;
                        request_number: number;
                        urgent: boolean;
                        pbm_only: boolean;
                        assigned_upn?: string;
                        payer_assigned_upn?: string;
                        issue_source?: string;
                        request_status: PayerRequestStatus;
                        escalated_to?: (typeof EscalatedTo)[keyof typeof EscalatedTo];
                        additional_notes?: string;
                        last_updated: string;
                        resolved_at?: string;
                    }[];
                },
                _,
                { payerId },
            ) =>
                response?.requests?.map((request) => ({
                    payerId,
                    requestId: request.request_id,
                    requestSubType: request.request_sub_type,
                    typeLevel2: request.type_level_2,
                    summary: request.summary,
                    additionalNotes: request.additional_notes,
                    memberId: request.member_id,
                    createDate: request.create_date,
                    submitterUpn: request.submitter_upn,
                    assignedUpn: request.assigned_upn,
                    payerAssignedUpn: request.payer_assigned_upn,
                    issueSource: request.issue_source,
                    isUnread: request.is_unread,
                    requestNumber: request.request_number?.toString().padStart(6, '0'),
                    urgent: request.urgent,
                    pbmOnly: request.pbm_only,
                    requestStatus: request.request_status,
                    escalatedTo: request.escalated_to,
                    lastUpdated: request.last_updated,
                    resolvedDate: request.resolved_at,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
            providesTags: [TagType.REQUEST_LIST],
        }),
        getArchivedRequests: build.query<
            {
                count: number;
                filters: { key: string; values: string[] }[];
                requests: ArchivedRequestSummary[];
            },
            {
                payerId: string;
                page: number;
                segmentIds: string[];
                searchText?: string;
                timeframe?: TimeFrameOption;
                requestType?: (typeof RequestType)[keyof typeof RequestType][];
                requestSubType?: string[];
                typeLevel2?: string[];
                issueSource?: string[];
                requestStatus?: PayerRequestStatus[];
                urgent?: boolean;
                pbmOnly?: boolean;
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({
                payerId,
                page,
                segmentIds,
                searchText,
                timeframe,
                requestType,
                requestSubType,
                typeLevel2,
                issueSource,
                requestStatus,
                urgent,
                pbmOnly,
                sort,
            }) => ({
                url: `${payerId}/requests/archive`,
                params: {
                    page,
                    segment_ids: segmentIds,
                    search_text: searchText,
                    timeframe,
                    request_type: requestType,
                    request_sub_type: requestSubType,
                    type_level_2: typeLevel2,
                    issue_source: issueSource,
                    request_status: requestStatus,
                    urgent,
                    pbm_only: pbmOnly,
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
            }),
            transformResponse: (
                response: {
                    count: number;
                    filters: { key: string; values: string[] }[];
                    requests: (Omit<
                        ArchivedRequestSummary,
                        | 'payer_id'
                        | 'request_number'
                        | 'submitter_full_name'
                        | 'completed_by_full_name'
                    > & {
                        request_number: number;
                    })[];
                },
                _,
                { payerId },
            ) => ({
                count: response?.count,
                filters: response?.filters,
                requests: response?.requests?.map((request) => ({
                    ...request,
                    payer_id: payerId,
                    request_number: request.request_number?.toString().padStart(6, '0'),
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
            providesTags: [TagType.REQUEST_LIST],
        }),
        getRequestDetails: build.query<RequestDetails, { payerId: string; requestId: string }>({
            query: ({ payerId, requestId }) => `${payerId}/requests/${requestId}`,
            // eslint-disable-next-line complexity
            transformResponse: (response: {
                request_id: string;
                request_type: Exclude<
                    (typeof RequestType)[keyof typeof RequestType],
                    typeof RequestType.ARCHIVE
                >;
                request_sub_type?: string;
                type_level_2?: string;
                summary?: string;
                segment_id: string;
                member_id?: string;
                first_name?: string;
                last_name?: string;
                date_of_birth?: string;
                benefit_group?: string;
                create_date: string;
                submitter_upn: string;
                request_number: number;
                reopened_from_request_id?: string;
                reopened_from_request_number?: number;
                issue_source?: string;
                primary_issue_description?: string;
                secondary_issue_description?: string;
                request_status: PayerRequestStatus;
                escalated_to: (typeof EscalatedTo)[keyof typeof EscalatedTo];
                assigned_upn?: string;
                payer_assigned_upn?: string;
                urgent: boolean;
                pbm_only: boolean;
                additional_notes?: string;
                treatment_ids: string[];
                attachments: {
                    request_attachment_id: string;
                    filename: string;
                    date_added: string;
                }[];
                activity: {
                    request_activity_id: string;
                    action_date: string;
                    action_type: (typeof RequestActionType)[keyof typeof RequestActionType];
                    actor_upn?: string;
                    fmh_action: boolean;
                    action_detail?: string;
                }[];
            }) => ({
                requestId: response?.request_id,
                segmentId: response?.segment_id,
                requestType: response?.request_type,
                requestSubType: response?.request_sub_type,
                typeLevel2: response?.type_level_2,
                summary: response?.summary,
                memberId: response?.member_id,
                firstName: response?.first_name,
                lastName: response?.last_name,
                dateOfBirth: response?.date_of_birth,
                benefitGroup: response?.benefit_group,
                createDate: response?.create_date,
                submitterUpn: response?.submitter_upn,
                assignedUpn: response?.assigned_upn,
                payerAssignedUpn: response?.payer_assigned_upn,
                requestNumber: response?.request_number?.toString().padStart(6, '0'),
                reopenedFromRequestId: response?.reopened_from_request_id,
                reopenedFromRequestNumber: response?.reopened_from_request_number
                    ?.toString()
                    .padStart(6, '0'),
                issueSource: response?.issue_source,
                primaryIssueDescription: response?.primary_issue_description,
                secondaryIssueDescription: response?.secondary_issue_description,
                requestStatus: response?.request_status,
                escalatedTo: response?.escalated_to,
                urgent: response?.urgent,
                pbmOnly: response?.pbm_only,
                additionalNotes: response?.additional_notes,
                treatmentIds: response?.treatment_ids,
                attachments: response?.attachments?.map((attachment) => ({
                    requestAttachmentId: attachment.request_attachment_id,
                    filename: attachment.filename,
                    dateAdded: attachment.date_added,
                })),
                activity: response?.activity?.map((action) => ({
                    requestActivityId: action.request_activity_id,
                    actionDate: action.action_date,
                    actionType: action.action_type,
                    actorUpn: action.actor_upn,
                    fmhAction: action.fmh_action,
                    actionDetail: action.action_detail,
                })),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving request details.',
            ),
            providesTags: (_result, _error, { requestId }) => [
                { type: TagType.REQUESTS, id: requestId },
            ],
        }),
        postNewRequest: build.mutation<string, RequestPayload>({
            query: ({
                payerId,
                segmentId,
                requestType,
                requestSubType,
                typeLevel2,
                memberId,
                firstName,
                lastName,
                dateOfBirth,
                urgent,
                pbmOnly,
                additionalNotes,
                treatmentIds,
                files,
                assignedUpn,
                payerAssignedUpn,
            }) => ({
                url: `${payerId}/requests`,
                method: 'POST',
                body: jsonToFormData({
                    segment_id: segmentId,
                    request_type: requestType,
                    request_sub_type: requestSubType,
                    type_level_2: typeLevel2,
                    member_id: memberId,
                    first_name: firstName,
                    last_name: lastName,
                    date_of_birth: dateOfBirth,
                    urgent,
                    pbm_only: pbmOnly,
                    additional_notes: additionalNotes,
                    treatment_ids: treatmentIds,
                    files,
                    assigned_upn: assignedUpn,
                    payer_assigned_upn: payerAssignedUpn,
                }),
            }),
            transformResponse: (response: { request_id: string }) =>
                response?.request_id?.toLocaleLowerCase(),
            transformErrorResponse: handleRTKQueryError('An error occurred creating request.'),
            invalidatesTags: (_, error) =>
                !error ? [TagType.REQUESTS_COUNTS, TagType.REQUEST_LIST] : [],
        }),
        postUpdateRequestDetails: build.mutation<void, { requestId: string } & RequestPayload>({
            query: ({
                payerId,
                requestId,
                segmentId,
                requestType,
                requestSubType,
                typeLevel2,
                memberId,
                firstName,
                lastName,
                dateOfBirth,
                urgent,
                pbmOnly,
                additionalNotes,
                treatmentIds,
                files,
                assignedUpn,
                payerAssignedUpn,
            }) => ({
                url: `${payerId}/requests/${requestId}/activity/detail_up`,
                method: 'POST',
                body: jsonToFormData({
                    segment_id: segmentId,
                    request_type: requestType,
                    request_sub_type: requestSubType,
                    type_level_2: typeLevel2,
                    member_id: memberId,
                    first_name: firstName,
                    last_name: lastName,
                    date_of_birth: dateOfBirth,
                    urgent,
                    pbm_only: pbmOnly,
                    additional_notes: additionalNotes,
                    treatment_ids: treatmentIds,
                    files,
                    assigned_upn: assignedUpn,
                    payer_assigned_upn: payerAssignedUpn,
                }),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred updating request.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error
                    ? [
                          { type: TagType.REQUESTS, id: requestId },
                          TagType.REQUEST_LIST,
                          TagType.REQUESTS_COUNTS,
                      ]
                    : [],
        }),
        postMarkRequestRead: build.mutation<void, { payerId: string; requestId: string }>({
            query: ({ payerId, requestId }) => ({
                url: `${payerId}/requests/${requestId}/read`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred marking request as read.',
            ),
            invalidatesTags: (_, error) =>
                !error ? [TagType.REQUESTS_COUNTS, TagType.REQUEST_LIST] : [],
        }),
        postRequestActivity: build.mutation<
            string,
            {
                payerId: string;
                requestId: string;
                actionType: (typeof RequestActionType)[keyof typeof RequestActionType];
                actionDetail?: string;
                // Resolve specific fields
                issueSource?: string;
                primaryIssueDescription?: string;
                secondaryIssueDescription?: string;
                skipTagInvalidation?: boolean;
            }
        >({
            query: ({
                payerId,
                requestId,
                actionType,
                actionDetail,
                issueSource,
                primaryIssueDescription,
                secondaryIssueDescription,
            }) => ({
                url: `${payerId}/requests/${requestId}/activity`,
                method: 'POST',
                body: {
                    action_type: actionType,
                    action_detail: actionDetail,
                    issue_source: issueSource,
                    primary_issue_description: primaryIssueDescription,
                    secondary_issue_description: secondaryIssueDescription,
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred updating request.'),
            invalidatesTags: (_, error, { requestId, skipTagInvalidation }) =>
                !error && !skipTagInvalidation
                    ? [
                          { type: TagType.REQUESTS, id: requestId },
                          TagType.REQUEST_LIST,
                          TagType.REQUESTS_COUNTS,
                      ]
                    : [],
        }),
        postResolveAndCloseRequest: build.mutation<
            string,
            {
                payerId: string;
                requestId: string;
                issueSource: string;
                primaryIssueDescription: string;
                secondaryIssueDescription?: string;
            }
        >({
            queryFn: async (
                {
                    payerId,
                    requestId,
                    issueSource,
                    primaryIssueDescription,
                    secondaryIssueDescription,
                },
                _queryApi,
                _extraOptions,
                baseQuery,
            ) => {
                // Writing as separate mutation to avoid early cache invalidation when calling same mutation twice
                const resolveResult = await baseQuery({
                    url: `${payerId}/requests/${requestId}/activity`,
                    method: 'POST',
                    body: {
                        action_type: RequestActionType.RESOLVED,
                        issue_source: issueSource,
                        primary_issue_description: primaryIssueDescription,
                        secondary_issue_description: secondaryIssueDescription,
                    },
                });
                if (resolveResult.error) {
                    handleError('An error occurred resolving request.', resolveResult.error);
                    return { error: resolveResult.error };
                }

                const closeResult = await baseQuery({
                    url: `${payerId}/requests/${requestId}/activity`,
                    method: 'POST',
                    body: { action_type: RequestActionType.COMPLETED },
                });
                if (closeResult.error) {
                    handleError('An error occurred closing request.', closeResult.error);
                    return { error: closeResult.error };
                }

                return { data: closeResult.data as string };
            },
            invalidatesTags: (_, error, { requestId }) =>
                !error
                    ? [
                          { type: TagType.REQUESTS, id: requestId },
                          TagType.REQUEST_LIST,
                          TagType.REQUESTS_COUNTS,
                      ]
                    : [],
        }),
        postAttachFile: build.mutation<
            void,
            { payerId: string; requestId: string; attachment: File }
        >({
            query: ({ payerId, requestId, attachment }) => ({
                url: `${payerId}/requests/${requestId}/attachment`,
                method: 'POST',
                body: jsonToFormData({ attachment }),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred uploading file.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error ? [{ type: TagType.REQUESTS, id: requestId }] : [],
        }),
        // Request Notes
        getNotesForRequest: build.query<RequestNote[], { payerId: string; requestId: string }>({
            query: ({ payerId, requestId }) => `${payerId}/requests/${requestId}/notes`,
            transformResponse: (
                response: {
                    request_note_id: string;
                    request_note: string;
                    upn: string;
                    sent_time: string;
                }[],
            ) =>
                response?.map((note) => ({
                    requestNoteId: note.request_note_id,
                    requestNote: note.request_note,
                    upn: note.upn,
                    sentTime: note.sent_time,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving notes.'),
            providesTags: (_result, _error, { requestId }) => [
                { type: TagType.REQUEST_NOTES, id: requestId },
            ],
        }),
        postRequestNote: build.mutation<
            void,
            { payerId: string; requestId: string; requestNote: string }
        >({
            query: ({ payerId, requestId, requestNote }) => ({
                url: `${payerId}/requests/${requestId}/notes`,
                method: 'POST',
                body: { request_note: requestNote },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred creating note.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error ? [{ type: TagType.REQUEST_NOTES, id: requestId }] : [],
        }),
        deleteRequestNote: build.mutation<
            void,
            { payerId: string; requestId: string; requestNoteId: string }
        >({
            query: ({ payerId, requestId, requestNoteId }) => ({
                url: `${payerId}/requests/${requestId}/notes/${requestNoteId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred deleting note.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error ? [{ type: TagType.REQUEST_NOTES, id: requestId }] : [],
        }),
    }),
});

export const {
    useGetOpenRequestCountsQuery,
    useGetOpenRequestsByTypeQuery,
    useGetArchivedRequestsQuery,
    useGetRequestDetailsQuery,
    usePostNewRequestMutation,
    usePostMarkRequestReadMutation,
    usePostRequestActivityMutation,
    usePostResolveAndCloseRequestMutation,
    usePostUpdateRequestDetailsMutation,
    usePostAttachFileMutation,
    useGetNotesForRequestQuery,
    useDeleteRequestNoteMutation,
    usePostRequestNoteMutation,
} = payerRequestApi;
